import { all, call, put, select, takeLatest } from 'redux-saga/effects'

import { getAccessToken } from './OngageUnsubscribeSelector'

import {
  endpointVersions,
  notificationDuration as duration
} from 'src/constants'
import { FIRST_WORD_ONGAGE_UNSUBSCRIBE } from './constants'

import { notificationsEnqueue } from 'src/features/notifications/NotificationsSlice'
import {
  fetchAuthTokenSuccess,
  fetchObfuscatedEmailSuccess,
  fetchListProfileSuccess,
  fetchListProfileFailure,
  postCampaignsBulkSubscriptionSuccess,
  postCampaignsBulkSubscriptionFailure
} from './OngageUnsubscribeSlice'

const {
  tokenGetEndpoint,
  userObfuscatedEmailEndpoint,
  userListCampaignsEndpoint,
  userBulkCampaignsSubscriptionsEndpoint
} = endpointVersions.V2

function* fetchPhoenixEngineAuthDataSaga(services, { payload }) {
  const { partyId, partyEmailId } = payload

  try {
    if (!partyId || !partyEmailId) {
      throw new Error('Missing IDH values.')
    }

    const PhoenixAuthService = services('PhoenixAuthService')

    const phoenixAuthParams = {
      partyId: Number(partyId),
      partyEmailId: Number(partyEmailId),
      loggedIn: true
    }

    // Fetching the token
    const phoenixTokenRes = yield call(
      [PhoenixAuthService, 'getPhoenixToken'],
      tokenGetEndpoint,
      phoenixAuthParams
    )
    yield put(fetchAuthTokenSuccess(phoenixTokenRes))

    // Fetching the obfuscated email
    const userEmail = yield call(
      [PhoenixAuthService, 'getObfuscatedEmail'],
      userObfuscatedEmailEndpoint,
      phoenixTokenRes.access
    )
    yield put(fetchObfuscatedEmailSuccess(userEmail.obfuscatedEmail))

    const payload = FIRST_WORD_ONGAGE_UNSUBSCRIBE.map(
      ({ description, ...rest }) => rest
    )

    const fetchProfileCampaigns = yield call(
      [PhoenixAuthService, 'getUserProfileCampaigns'],
      `${userListCampaignsEndpoint}?idType=EmailID`,
      phoenixTokenRes.access,
      payload
    )

    const responseMap = fetchProfileCampaigns.campaigns.reduce(
      (accumulated, campaign) => {
        const key = `${campaign.campaignId}|${campaign.brandId}|${campaign.siteId}`
        accumulated[key] = campaign.isSubscribed
        return accumulated
      },
      {}
    )

    const mergedData = FIRST_WORD_ONGAGE_UNSUBSCRIBE.map(campaign => {
      const key = `${campaign.campaignId}|${campaign.brandId}|${campaign.siteId}`
      return {
        ...campaign,
        ...(responseMap[key] != null && { isSubscribed: responseMap[key] })
      }
    })
    yield put(fetchListProfileSuccess(mergedData))
  } catch (error) {
    yield put(fetchListProfileFailure(error.message))
  }
}

function* requestNewsletterUnsubscribeSaga(services, { payload }) {
  try {
    const accessToken = yield select(getAccessToken)
    if (!accessToken) {
      throw new Error('Skipping fetch because missing values.')
    }

    const PhoenixAuthService = services('PhoenixAuthService')

    yield call(
      [PhoenixAuthService, 'postUserNewsletterCampaignsBulkSubscriptions'],
      userBulkCampaignsSubscriptionsEndpoint,
      accessToken,
      payload
    )
    yield put(postCampaignsBulkSubscriptionSuccess())
  } catch (error) {
    yield put(postCampaignsBulkSubscriptionFailure(error.message))
    yield put(
      notificationsEnqueue({
        message: `Something went wrong. Please refresh the page and try again.`,
        duration
      })
    )
  }
}

function* watchOngageUnsubscribeSaga(services) {
  yield all([
    takeLatest(
      'ongageUnsubscribe/fetchOngageUnsubscribeRequest',
      fetchPhoenixEngineAuthDataSaga,
      services
    ),
    takeLatest(
      'ongageUnsubscribe/postCampaignsBulkSubscriptionRequest',
      requestNewsletterUnsubscribeSaga,
      services
    )
  ])
}

export default watchOngageUnsubscribeSaga
